import React, { ReactNode, useEffect, useState } from 'react';

import '../Home.scss';
import { Button, ModalHeader, ModalBody, ModalFooter, Dropdown, Surface, Row, Col, Typography, MessageBlockContent, MessageBlock, TextField } from 'cfa-react-components';
import { ICapture, ICaptureConfig, IReplayConfig } from 'interfaces';
import { CaptureConfigService, CaptureService, ReplayConfigService, ReplayService, parseAxiosError } from 'services';

interface IReplayModalProps {
    selectedCapture?: ICapture,
    onHide: () => void,
    refresh: () => void,
}

export function ReplayModal({ selectedCapture, onHide, refresh }: IReplayModalProps) {
    
    const maxCaptureDuration = 3600;
    const maxMessageCount = 1000;

    const [config, setConfig] = useState<IReplayConfig | null>(null);
    const [replayName, setReplayName] = useState<string>("");
    const [capture, setCapture] = useState<ICapture | null>(null);

    const [loading, setLoading] = useState(false);
    const [warning, setWarning] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const setAndValidateConfig = (cfg: IReplayConfig) => {
        setConfig(cfg);

        let w = "";
        let rule1 = cfg.sinkType+cfg.sinkParams.location == capConfig.sourceType+capConfig.sourceParams.location;
        let rule2 = (cfg.timingOption == "stated" || cfg.timingOption == "actual") && capConfig.durationMinutes > maxCaptureDuration;
        let rule3 = capture.messageCount > maxMessageCount;

        if (rule1) w += " Source and Sink are the same.";
        if (rule2) w += " Replay duration exceeds " + maxCaptureDuration + " minutes.";
        if (rule3) w += " Message count exceeds " + maxMessageCount + " messages.";
        
        if (w != "") setWarning(w);
        else setWarning(null);
    }

    const submitReplay = () => {
        if (!config || replayName == "") return;

        setLoading(true);
        setErrorMessage(null);

        ReplayService.postReplay(replayName, capture, config)
            .then(() => {
                refresh();
                onHide();
            })
            .catch((e) => {
                setErrorMessage(parseAxiosError(e));
                setLoading(false);
            })
    }

    const capConfig: ICaptureConfig = capture ? CaptureConfigService.allCaptureConfigsMap[capture.configUUID] : undefined;

    useEffect(() => {
        // make sure the capture gets set if a selectedCapture is passed in
        if (selectedCapture) {
            setCapture(selectedCapture);
        }
    });
    
    const captureSurfaceContent: ReactNode = capture && (
        <div style={{width: '100%', textAlign: 'left', fontSize: '14px', lineHeight: '18px', paddingLeft: 15}}>
            <Row>
                <Col xs={2}>{'Type:'}</Col>
                <Col xs={10}>{capConfig.sourceType}</Col>
            </Row>
            <Row className='noTopMargin'>
                <Col xs={2}>{'Source:'}</Col>
                <Col xs={10}>{capConfig.sourceParams.location}</Col>
            </Row>
            <Row className='noTopMargin rowLastItem'>
                <Col xs={2}>{'Secret:'}</Col>
                <Col xs={10}>{capConfig.sourceParams.secretPath}</Col>
            </Row>
        </div>
    );

    const configSurfaceContent: ReactNode = config && (
        <div style={{width: '100%', textAlign: 'left', fontSize: '14px', lineHeight: '18px', paddingLeft: 15}}>
            <Row>
                <Col xs={2}>{'Type:'}</Col>
                <Col xs={10}>{config.sinkType}</Col>
            </Row>
            <Row className='noTopMargin'>
                <Col xs={2}>{'Sink:'}</Col>
                <Col xs={10}>{config.sinkParams.location}</Col>
            </Row>
            <Row className='noTopMargin'>
                <Col xs={2}>{'Secret:'}</Col>
                <Col xs={10}>{config.sinkParams.secretPath}</Col>
            </Row>
            <Row className='noTopMargin rowLastItem'>
                <Col xs={2}>{'Timing:'}</Col>
                <Col xs={10}>{config.timingOption}</Col>
            </Row>
        </div>
    );

    return (<>

        <ModalHeader>
            Start Replay
        </ModalHeader>

        <ModalBody>
            <Row>
                <Typography>
                    Directions: Choose a capture and a replay configuration from the options below, 
                    then start the replay. Note that the replays may take a long time, 
                    so be careful not to start too many at once.
                </Typography>
            </Row>
            {warning && (<Row style={{justifyContent: 'center'}}>
                <MessageBlock severity='warning'>
                    <MessageBlockContent>{warning.toString()}</MessageBlockContent>
                </MessageBlock>
            </Row>)}
            {errorMessage && (<Row style={{justifyContent: 'center'}}>
                <MessageBlock severity='error'>
                    <MessageBlockContent>{errorMessage.toString()}</MessageBlockContent>
                </MessageBlock>
            </Row>)}
            <Row>
                <Col>
                    <TextField
                        style={{width: '100%', paddingBottom: '10px'}}
                        placeholder='Enter Replay Name'
                        onChange={(e) => setReplayName(e.target.value)}
                    />
                    <Dropdown
                        style={{width: '100%'}}
                        placeholder={selectedCapture ? selectedCapture.name :'Select Capture' }
                        value={capture}
                        onChange={(newVal) => setCapture(newVal)}
                        options={selectedCapture ? [selectedCapture] : CaptureService.allCaptures}
                        renderOption={(c) => <>{c.name}</>}
                        // only disable if item passed in
                        disabled={!!selectedCapture}
                        // must have these options, since we're passing in an array of objects
                        getOptionText={(c) => c.name}
                        getOptionId={(c) => c.captureUUID}
                    />
                </Col>
                <Col>
                    <Surface
                        as="div"
                        elevation={1}
                        radii="md"
                        style={{
                            justifyContent: 'center',
                            display: 'flex',
                            height: '100%',
                            width: '100%'
                        }}
                        variant="default"
                    >
                        {captureSurfaceContent}
                    </Surface>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Dropdown
                        style={{width: '100%'}}
                        placeholder='Select Replay Config'
                        value={config}
                        onChange={(newVal) => setAndValidateConfig(newVal)}
                        options={ReplayConfigService.allReplayConfigs}
                        renderOption={(r) => <>{r.name}</>}
                        // must have these options, since we're passing in an array of objects
                        getOptionText={(r) => r.name}
                        getOptionId={(r) => r.replayConfigUUID}
                    />
                </Col>
                <Col>
                    <Surface
                        as="div"
                        elevation={1}
                        radii="md"
                        style={{
                            justifyContent: 'center',
                            display: 'flex',
                            height: '100%',
                            width: '100%'
                        }}
                        variant="default"
                    >
                        {configSurfaceContent}
                    </Surface>
                </Col>
            </Row>
        </ModalBody>

        <ModalFooter>
            <Button variant="outlined" onClick={onHide}>
                Close
            </Button>
            <Button 
                variant="filled" 
                onClick={submitReplay}
                disabled={!config || replayName == ""}
                loading={loading}
            >
                {loading ? 'Submitting...' : 'Submit'}
            </Button>
        </ModalFooter>

    </>);
}